import { setActiveCatalogue, type Catalogue } from "./catalogue.js";
import {
  applyResolvedLanguage,
  resolveLanguage,
  setLanguageChoice,
  type Language,
  type LanguageChoice,
  type ResolvedLanguage,
} from "./language.js";

/**
 * `@gladis/ui/i18n` — the language preference, the catalogue, and the date formatters.
 *
 * ==========================================================================================
 * THIS MODULE STATICALLY IMPORTS NEITHER CATALOGUE, AND THAT IS THE POINT
 * ==========================================================================================
 *
 * `nl.ts` and `en.ts` are reached ONLY through the dynamic `import()` in `loadCatalogue` below. A
 * static `export * from "./nl.js"` here would put BOTH languages' copy in whichever chunk first
 * imports anything from this subpath — which is every component in the application — so every
 * reader would download the language they are not reading.
 *
 * It is also why the subpath exists at all. `@gladis/ui/motion` is a subpath because reaching the
 * package index for a hook dragged the whole component library onto the first-paint path, and the
 * only symptom was a one-in-four flake in an e2e spec three features away. Same mechanism, applied
 * before the flake rather than after it. `first-paint-imports.test.ts` fires on the import.
 *
 * ==========================================================================================
 * WHY A SUBPATH OF `@gladis/ui` AND NOT A PACKAGE OF ITS OWN
 * ==========================================================================================
 *
 * `@gladis/ui` is already an optional peer dependency of every domain package, so every domain
 * `ui/` subpath can reach this with NO manifest change and no domain package gaining a dependency —
 * which is what keeps the constitution's clause about runtime dependencies in domain packages out
 * of this feature entirely. A `@gladis/i18n` package would have needed adding to seven manifests to
 * buy a boundary this already provides.
 *
 * `@gladis/shared` is the wrong home for the opposite reason: `apps/api` imports it, and Dutch
 * interface copy has no business in the API's dependency tree, where nothing will ever read it.
 */

/**
 * `setLanguageChoice` IS DELIBERATELY NOT RE-EXPORTED HERE. Use `changeLanguage` below.
 *
 * Setting the choice without loading the target catalogue first leaves the preference, the `lang`
 * attribute and the money locale on the new language while every word on screen stays on the old
 * one — a half-switched application, and one that looks like a translation gap rather than a wiring
 * mistake. The correct call is the only one reachable, which is the same discipline that stops
 * anyone obtaining a database handle outside the chokepoint.
 */
export {
  applyResolvedLanguage,
  readLanguageChoice,
  resolveLanguage,
  useLanguage,
  LANGUAGE_ATTRIBUTE,
  LANGUAGE_STORAGE_KEY,
  type Language,
  type LanguageChoice,
  type LanguageLocale,
  type LanguageState,
  type ResolvedLanguage,
} from "./language.js";

export {
  activeCatalogueLanguage,
  activeLocale,
  setActiveCatalogue,
  t,
  useT,
  type Catalogue,
  type CatalogueEntry,
  type TranslateFn,
  type TranslateParams,
} from "./catalogue.js";

export {
  formatDate,
  formatDateTime,
  formatDayAndMonth,
  formatMonth,
  formatMonthOnly,
  type DateStyle,
} from "./format.js";

export { tRich } from "./rich.js";

export { errorSentence, expenseCategoryLabel, incomeCategoryLabel } from "./errors.js";

/**
 * Fetches ONE language's catalogue. The dynamic `import()` is what splits them into two chunks.
 *
 * Cached, so switching back and forth costs one fetch per language for the life of the page.
 */
const loaded = new Map<Language, Catalogue>();

async function catalogueFor(language: Language): Promise<Catalogue> {
  const cached = loaded.get(language);
  if (cached !== undefined) return cached;

  const catalogue = language === "nl" ? (await import("./nl.js")).nl : (await import("./en.js")).en;
  loaded.set(language, catalogue);
  return catalogue;
}

/**
 * Resolves the language, fetches its catalogue, and installs it.
 *
 * CALLED ONCE, ABOVE `createRoot().render()`, AND AWAITED. `apps/app/src/main.tsx` carries the
 * argument in full, and it is the money locale's argument with far more surface: resolving in an
 * effect runs after the first paint, so the first screen renders in one language and changes to
 * another under the reader. On the dashboard that is every figure and every label rewriting itself,
 * which reads as a bug and, for the half-second it lasts, is a wrong statement about money.
 */
export async function loadCatalogue(): Promise<ResolvedLanguage> {
  const resolved = applyResolvedLanguage();
  setActiveCatalogue(resolved.language, resolved.locale, await catalogueFor(resolved.language));
  return resolved;
}

/**
 * Changes the language: fetch first, then switch, so the interface never renders half-translated.
 *
 * THE ORDER IS THE WHOLE FUNCTION. `setLanguageChoice` stamps `<html lang>`, sets the money locale
 * and re-renders every subscriber; if the catalogue were not already installed, all of that would
 * happen against the OLD words. Awaiting the fetch first means the switch is one atomic re-render —
 * which is what FR-015 asks for, and what a reader watching the page expects.
 *
 * The await costs nothing after the first switch in each direction: the module is cached above, and
 * on a warm cache both branches resolve in the same microtask.
 */
export async function changeLanguage(choice: LanguageChoice): Promise<ResolvedLanguage> {
  const target = resolveLanguage(choice);
  setActiveCatalogue(target.language, target.locale, await catalogueFor(target.language));
  setLanguageChoice(choice);
  return target;
}
